import React, { useState, useRef, useEffect } from 'react';
import { Check, AlertOctagon, MessageSquare, X, Loader2, AlertCircle } from 'lucide-react';
import T from 'Components/Helpers/translate';

export type StatusPill = {
	label: string;
	tone: 'pending' | 'saving' | 'ok' | 'muted' | 'error';
};

export type ProposalCardShellProps = {
	icon: React.ComponentType<{ style?: React.CSSProperties }>;
	title: string;
	chips?: string[];
	typePill?: string | null;
	statusPill?: StatusPill | null;
	/** Optional control rendered inline in the card header, after the
	 *  status pill. Used by resolved cards to expose a "View" link without
	 *  taking vertical space in the body. */
	headerAction?: React.ReactNode;
	body: React.ReactNode;
	busy?: boolean;
	resolved?: boolean;
	approveLabel?: string;
	rejectLabel?: string;
	onApprove?: () => void;
	onReject?: () => void;
	onRequestChanges?: (note: string) => void;
	/** An extra secondary button, rendered before the primary one. Used by the
	 *  failed state to offer "ask the assistant to fix it" alongside Retry —
	 *  reject/request-changes are not available on a resolved row (WEB-9873). */
	secondaryAction?: { label: string; onClick: () => void; testId?: string };
	defaultExpanded?: boolean;
};

const STATUS_ICONS: Record<StatusPill['tone'], React.ComponentType<{ style?: React.CSSProperties }> | null> = {
	pending: null,
	saving: Loader2,
	ok: Check,
	muted: X,
	error: AlertCircle
};

const StatusPillView = ({ pill }: { pill: StatusPill }) => {
	const Icon = STATUS_ICONS[pill.tone];
	return (
		<span className={`ws-prop-card-status ws-prop-card-status--${pill.tone}`}>
			{Icon && (
				<Icon
					style={{
						width: 11,
						height: 11,
						animation: pill.tone === 'saving' ? 'ws-spin 1s linear infinite' : undefined
					}}
				/>
			)}
			<span>{pill.label}</span>
		</span>
	);
};

const ProposalCardShell = ({
	icon: Icon,
	title,
	chips = [],
	typePill,
	statusPill,
	headerAction,
	body,
	busy = false,
	resolved = false,
	approveLabel,
	rejectLabel,
	onApprove,
	onReject,
	onRequestChanges,
	secondaryAction,
	defaultExpanded = false
}: ProposalCardShellProps) => {
	const [expanded, setExpanded] = useState(defaultExpanded);
	const [notesMode, setNotesMode] = useState(false);
	const [note, setNote] = useState('');
	const noteRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (notesMode) noteRef.current?.focus();
	}, [notesMode]);

	const stop = (e: React.MouseEvent) => e.stopPropagation();

	const submitRequestChanges = () => {
		if (!onRequestChanges) return;
		const text = note.trim();
		if (!text) {
			noteRef.current?.focus();
			return;
		}
		onRequestChanges(text);
		setNote('');
		setNotesMode(false);
	};

	const cancelNotes = () => {
		setNote('');
		setNotesMode(false);
	};

	if (!expanded) {
		return (
			<div
				className="ws-prop-card ws-prop-card--compact"
				role="button"
				tabIndex={0}
				onClick={() => setExpanded(true)}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						setExpanded(true);
					}
				}}
			>
				<Icon style={{ width: 18, height: 18, color: 'var(--ws-ink-3)', flexShrink: 0 }} />
				<div className="ws-prop-card-summary">
					<div className="ws-prop-card-title-row">
						<div className="ws-prop-card-title">{title}</div>
						{headerAction && (
							<span className="ws-prop-card-header-action" onClick={stop}>
								{headerAction}
							</span>
						)}
						{statusPill && <StatusPillView pill={statusPill} />}
						{!resolved && (
							<div className="ws-prop-card-hover-actions" onClick={stop}>
								{onReject && (
									<button
										type="button"
										className="ws-btn ws-btn-secondary ws-prop-mini-btn"
										onClick={onReject}
										disabled={busy}
									>
										<span>{rejectLabel ?? T('assistant.reject')}</span>
									</button>
								)}
								{onApprove && (
									<button
										type="button"
										className="ws-btn ws-btn-primary ws-prop-mini-btn"
										onClick={onApprove}
										disabled={busy}
									>
										<Check style={{ width: 12, height: 12 }} />
										<span>{approveLabel ?? T('assistant.approve')}</span>
									</button>
								)}
							</div>
						)}
					</div>
					{(chips.length > 0 || typePill) && (
						<div className="ws-prop-card-meta">
							{chips.map((c, i) => (
								<span key={i} className="ws-prop-card-chip">
									{c}
								</span>
							))}
							{typePill && <span className="ws-prop-card-typepill">{typePill}</span>}
						</div>
					)}
				</div>
			</div>
		);
	}

	const collapse = () => {
		setExpanded(false);
		cancelNotes();
	};

	const hasActions = !resolved && (onApprove || onReject || onRequestChanges || secondaryAction);

	return (
		<div className="ws-prop-card ws-prop-card--expanded">
			<button
				type="button"
				className="ws-prop-card-head"
				onClick={collapse}
				aria-label={T('assistant.collapse') || 'Collapse'}
			>
				<Icon style={{ width: 18, height: 18, color: 'var(--ws-ink-3)', flexShrink: 0 }} />
				<div className="ws-prop-card-title">{title}</div>
				{headerAction && (
					<span className="ws-prop-card-header-action" onClick={stop}>
						{headerAction}
					</span>
				)}
				{statusPill && <StatusPillView pill={statusPill} />}
			</button>
			<div className="ws-prop-card-body">{body}</div>
			{notesMode && onRequestChanges && (
				<div className="ws-prop-card-note">
					<textarea
						ref={noteRef}
						className="ws-prop-card-note-input"
						placeholder={T('assistant.addNoteHint')}
						value={note}
						onChange={e => setNote(e.target.value)}
						rows={3}
					/>
				</div>
			)}
			{hasActions && (
				<div className="ws-prop-card-actions">
					{notesMode ? (
						<>
							<button
								type="button"
								className="ws-btn ws-btn-secondary ws-prop-mini-btn"
								onClick={cancelNotes}
								disabled={busy}
							>
								<span>{T('cancel') || T('assistant.cancel')}</span>
							</button>
							<button
								type="button"
								className="ws-btn ws-btn-primary ws-prop-mini-btn"
								onClick={submitRequestChanges}
								disabled={busy || !note.trim()}
							>
								<MessageSquare style={{ width: 12, height: 12 }} />
								<span>{T('assistant.requestChanges')}</span>
							</button>
						</>
					) : (
						<>
							{onReject && (
								<button
									type="button"
									className="ws-btn ws-btn-secondary ws-prop-mini-btn"
									onClick={onReject}
									disabled={busy}
									data-testid="proposal-reject"
								>
									<AlertOctagon style={{ width: 12, height: 12 }} />
									<span>{rejectLabel ?? T('assistant.reject')}</span>
								</button>
							)}
							{secondaryAction && (
								<button
									type="button"
									className="ws-btn ws-btn-secondary ws-prop-mini-btn"
									onClick={secondaryAction.onClick}
									disabled={busy}
									data-testid={secondaryAction.testId ?? 'proposal-secondary'}
								>
									<MessageSquare style={{ width: 12, height: 12 }} />
									<span>{secondaryAction.label}</span>
								</button>
							)}
							{onRequestChanges && (
								<button
									type="button"
									className="ws-btn ws-btn-secondary ws-prop-mini-btn"
									onClick={() => setNotesMode(true)}
									disabled={busy}
									data-testid="proposal-request-changes"
								>
									<MessageSquare style={{ width: 12, height: 12 }} />
									<span>{T('assistant.requestChanges')}</span>
								</button>
							)}
							{onApprove && (
								<button
									type="button"
									className="ws-btn ws-btn-primary ws-prop-mini-btn"
									onClick={onApprove}
									disabled={busy}
									data-testid="proposal-approve"
								>
									<Check style={{ width: 12, height: 12 }} />
									<span>{approveLabel ?? T('assistant.approve')}</span>
								</button>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default ProposalCardShell;
